/**
 * Converts a GraphQL sort object { property: 'a' | 'b'; direction: 'asc' | 'desc' } to a 'a' | '-a' | 'b' | '-b'
 *
 * @see toGraphQLSort to convert the other way
 */
type GqlSortObjectToString<T> =
  Exclude<T, undefined | null> extends { property: infer P } ? (P extends string ? P | `-${P}` : never) : never;

/**
 * Helper which converts graphql variables with sort object to string
 *
 * The sort object is converted
 *    from    { property: 'a' | 'b'; direction: 'asc' | 'desc' }
 *    to      a 'a' | '-a' | 'b' | '-b'
 *
 * Usage: GqlSortToString<VariablesOf<typeof SOME_QUERY>>;
 */
export type GqlSortToString<T extends { sort?: { property: string; direction?: 'asc' | 'desc' | null } | null }> = Omit<
  T,
  'sort'
> & { sort?: GqlSortObjectToString<T['sort']> };

const startWithDash = /^-/;

export const toGraphQLSort = <
  TPropertyWithDirection extends string | undefined,
  TPropertyName = TPropertyWithDirection extends `-${infer TPropertyWithoutDirection}`
    ? TPropertyWithoutDirection
    : Exclude<TPropertyWithDirection, undefined>,
  TReturn = TPropertyWithDirection extends string ? { property: TPropertyName; direction: 'asc' | 'desc' } : undefined,
>(
  value: TPropertyWithDirection
): TReturn => {
  if (typeof value === 'undefined') return undefined as TReturn;

  const property = value.replace(startWithDash, '');
  const direction = value.startsWith('-') ? 'desc' : 'asc';

  return { property, direction } as TReturn;
};
